/**
 * Partner Link Generator (admin tool).
 *
 * Builds a UTM-tagged URL to one of the audience landing pages so a
 * partner driving traffic can be attributed back to a specific
 * source / medium / campaign. The generated URL gets captured by
 * AudienceLandingPage on visit, persisted to sessionStorage, then
 * forwarded to /leads/submit when the visitor completes intake — so
 * every lead row ends up tagged with who sent them.
 *
 * The URL is built client-side from the form inputs and the host
 * inferred from window.location. "Save" persists it to the backend
 * (partner_links table) so links can be re-copied later — each saved
 * link shows how many leads it has produced.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Check, Link as LinkIcon, Save, Trash2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';

type Audience = 'family-offices' | 'for-funds';

interface SavedLink {
  id: number;
  audience: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content?: string | null;
  utm_term?: string | null;
  url: string;
  created_at?: string | null;
  lead_count: number;
}

interface AudienceOption {
  value: Audience;
  label: string;
  description: string;
}

const AUDIENCE_OPTIONS: AudienceOption[] = [
  {
    value: 'family-offices',
    label: 'Family offices',
    description: '/family-offices landing page',
  },
  {
    value: 'for-funds',
    label: 'Small VC funds',
    description: '/for-funds landing page',
  },
];

const MEDIUM_PRESETS = [
  'referral',
  'email',
  'paid_social',
  'organic_social',
  'newsletter',
  'webinar',
  'event',
  'partner',
];

/** Sanitize free-form UTM input. Lowercase + replace whitespace/specials
 *  with underscores so partner names and campaigns stay machine-friendly. */
function sanitizeUtm(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const PartnerLinkPage: React.FC = () => {
  const [audience, setAudience] = useState<Audience>('family-offices');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('referral');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedLinks, setSavedLinks] = useState<SavedLink[]>([]);
  const [savedLoading, setSavedLoading] = useState(true);

  const host = useMemo(() => {
    if (typeof window === 'undefined') return 'https://getventureos.ai';
    return `${window.location.protocol}//${window.location.host}`;
  }, []);

  const cleanedSource = sanitizeUtm(utmSource);
  const cleanedMedium = sanitizeUtm(utmMedium);
  const cleanedCampaign = sanitizeUtm(utmCampaign);
  const cleanedContent = sanitizeUtm(utmContent);
  const cleanedTerm = sanitizeUtm(utmTerm);

  const requiredOk = !!(cleanedSource && cleanedMedium && cleanedCampaign);

  const generatedUrl = useMemo(() => {
    if (!requiredOk) return '';
    const params = new URLSearchParams();
    params.set('utm_source', cleanedSource);
    params.set('utm_medium', cleanedMedium);
    params.set('utm_campaign', cleanedCampaign);
    if (cleanedContent) params.set('utm_content', cleanedContent);
    if (cleanedTerm) params.set('utm_term', cleanedTerm);
    return `${host}/${audience}?${params.toString()}`;
  }, [host, audience, requiredOk, cleanedSource, cleanedMedium, cleanedCampaign, cleanedContent, cleanedTerm]);

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — select the URL and copy manually.');
    }
  };

  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const resp = await api.get<SavedLink[]>('/partner-links');
      setSavedLinks(resp.data || []);
    } catch {
      // Non-fatal: the generator still works without the saved list.
    } finally {
      setSavedLoading(false);
    }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const handleSave = async () => {
    if (!generatedUrl) return;
    setSaving(true);
    try {
      await api.post('/partner-links', {
        audience,
        utm_source: cleanedSource,
        utm_medium: cleanedMedium,
        utm_campaign: cleanedCampaign,
        utm_content: cleanedContent || null,
        utm_term: cleanedTerm || null,
        url: generatedUrl,
      });
      toast.success('Link saved');
      loadSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not save the link');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/partner-links/${id}`);
      setSavedLinks((ls) => ls.filter((l) => l.id !== id));
      toast.success('Link removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not delete the link');
    }
  };

  const copySaved = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy — select the URL and copy manually.');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        <header className="mb-8">
          <div className="flex items-center gap-3">
            <LinkIcon className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Partner link generator</h1>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Build a UTM-tagged URL to hand to a partner who's driving traffic to one
            of our landing pages. Every lead they send through the link will be
            tagged with the source / medium / campaign you set here, so attribution
            shows up in the leads admin view automatically.
          </p>
        </header>

        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          <section>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Destination landing page
            </label>
            <div className="grid grid-cols-2 gap-3">
              {AUDIENCE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAudience(opt.value)}
                  className={`text-left rounded-lg border px-4 py-3 transition-colors ${
                    audience === opt.value
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
                </button>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="utm_source (required)"
              value={utmSource}
              onChange={setUtmSource}
              placeholder="acme_advisors"
              hint="Who is sending the traffic? Lowercase, underscores."
              cleaned={cleanedSource}
            />
            <Field
              label="utm_medium (required)"
              value={utmMedium}
              onChange={setUtmMedium}
              placeholder="referral"
              hint="How are they sending it?"
              cleaned={cleanedMedium}
              suggestions={MEDIUM_PRESETS}
            />
            <Field
              label="utm_campaign (required)"
              value={utmCampaign}
              onChange={setUtmCampaign}
              placeholder="summer_2026"
              hint="Which campaign / push?"
              cleaned={cleanedCampaign}
            />
            <Field
              label="utm_content (optional)"
              value={utmContent}
              onChange={setUtmContent}
              placeholder="cta_v1"
              hint="A/B variant or specific ad slot."
              cleaned={cleanedContent}
            />
            <Field
              label="utm_term (optional)"
              value={utmTerm}
              onChange={setUtmTerm}
              placeholder="family_office_tools"
              hint="Keyword (paid search only)."
              cleaned={cleanedTerm}
            />
          </section>

          <section>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Generated link
            </label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={generatedUrl}
                placeholder={requiredOk ? '' : 'Fill in source, medium, and campaign above…'}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono bg-gray-50 text-gray-800 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopy}
                disabled={!requiredOk}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!requiredOk || saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-blue-600 text-blue-700 bg-white text-sm font-medium hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Hand this single URL to the partner. If the same partner is using
              multiple channels (e.g., an email blast AND a LinkedIn post), generate
              one link per channel with different <code>utm_medium</code> / <code>utm_campaign</code>{' '}
              values so you can tell what worked.
            </p>
          </section>
        </div>

        {/* Saved links */}
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
          <h2 className="text-sm font-semibold text-gray-900">Saved links</h2>
          {savedLoading ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : savedLinks.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">
              Nothing saved yet — generate a link above and hit Save to keep it here.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-gray-100">
              {savedLinks.map((l) => (
                <li key={l.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900">{l.utm_source}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-600">{l.utm_medium}</span>
                      <span className="text-gray-400">·</span>
                      <span className="text-gray-600">{l.utm_campaign}</span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {l.audience}
                      </span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          l.lead_count > 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {l.lead_count} lead{l.lead_count === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="mt-1 truncate font-mono text-xs text-gray-500">
                      {l.url}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => copySaved(l.url)}
                    title="Copy link"
                    className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(l.id)}
                    title="Delete saved link"
                    className="rounded-lg border border-gray-300 p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900">
          <p className="font-semibold mb-1">Attribution lookup</p>
          <p>
            Find leads from this partner later by filtering the admin leads view by{' '}
            <code className="bg-white px-1.5 py-0.5 rounded text-xs">utm_source = {cleanedSource || 'partner_name'}</code>.
            Every submission through the tagged link writes those fields to the{' '}
            <code className="bg-white px-1.5 py-0.5 rounded text-xs">leads</code> table.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  cleaned?: string;
  suggestions?: string[];
}> = ({ label, value, onChange, placeholder, hint, cleaned, suggestions }) => {
  const showCleanedHint = !!cleaned && cleaned !== value.trim().toLowerCase();
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        list={suggestions ? `${label}-suggestions` : undefined}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
      />
      {suggestions && (
        <datalist id={`${label}-suggestions`}>
          {suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
      {showCleanedHint && (
        <p className="mt-1 text-xs text-amber-700">
          Will be saved as <code>{cleaned}</code>
        </p>
      )}
    </div>
  );
};

export default PartnerLinkPage;
